"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, CreditCard, Sparkles, TrendingUp } from "lucide-react";
import Link from "next/link";
import { useApi } from "@/lib/fetch-client";
import { formatCredits } from "@/lib/format-credits";
import { useI18n } from "@llmgateway/i18n/react";
import { TopUpCreditsDialog } from "./top-up-credits-dialog";
interface Organization {
  id: string;
  name: string;
  credits: string;
  plan: "free" | "pro" | "enterprise";
}
interface CreditsDisplayProps {
  organization: Organization | null;
  isLoading?: boolean;
  // True when showing the dedicated chat-plan ("Chat plan") context rather than a
  // real dashboard org. The chat plan and its upgrade nudge only apply here.
  isChatPlanOrg?: boolean;
}
export function CreditsDisplay(t0) {
  const $ = _c(50);
  const {
    organization,
    isLoading,
    isChatPlanOrg: t1
  } = t0;
  const isChatPlanOrg = t1 === undefined ? false : t1;
  const api = useApi();
  const {
    t
  } = useI18n();
  let t2;
  if ($[0] !== api) {
    t2 = api.queryOptions("get", "/chat-plans/status");
    $[0] = api;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = Boolean(organization) && isChatPlanOrg;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = {
      ...t2,
      enabled: t3,
      staleTime: 30000
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const planQuery = useQuery(t4);
  const plan = planQuery.data;
  const hasActivePlan = isChatPlanOrg && plan && plan.chatPlan !== "none";
  if (isLoading) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <CreditCard className="h-4 w-4 text-muted-foreground" />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t) {
      t6 = t("billing.credits");
      $[6] = t;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t6) {
      t7 = <span className="text-sm font-medium">{t6}</span>;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t) {
      t8 = t("common.loading");
      $[10] = t;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== t8) {
      t9 = <span className="text-xs text-muted-foreground">{t8}</span>;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== t7 || $[15] !== t9) {
      t10 = <div className="px-2 py-1.5"><div className="w-full flex items-center justify-between p-2 rounded-md"><div className="flex items-center gap-2">{t5}<div className="flex flex-col">{t7}{t9}</div></div></div></div>;
      $[14] = t7;
      $[15] = t9;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    return t10;
  }
  let t5;
  if ($[17] !== organization) {
    t5 = organization ? Number(organization.credits).toFixed(2) : "0.00";
    $[17] = organization;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const creditsBalance = t5;
  const planRemaining = hasActivePlan ? Number(plan.chatPlanCreditsRemaining) : 0;
  const totalAvailable = Number(creditsBalance) + planRemaining;
  const isLowCredits = organization && totalAvailable < 1;
  const hasNoCredits = organization && totalAvailable <= 0;
  let t6;
  if ($[19] !== hasActivePlan || $[20] !== plan || $[21] !== planRemaining || $[22] !== t) {
    t6 = hasActivePlan && plan && <Link href="/pricing" className="mb-1 block rounded-md border border-emerald-500/20 bg-emerald-500/5 px-2 py-1.5 hover:bg-emerald-500/10 transition-colors"><div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2 min-w-0"><Sparkles className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" /><div className="flex flex-col min-w-0"><span className="text-xs font-medium truncate">{t("pricing.plans." + plan.chatPlan + ".name")}{" "}{t("entities.chatPlan")}</span><span className="text-[10px] text-muted-foreground tabular-nums">{t("billing.chatPlanLeft", {
                remaining: formatCredits(planRemaining),
                limit: formatCredits(Number(plan.chatPlanCreditsLimit))
              })}</span></div></div></div></Link>;
    $[19] = hasActivePlan;
    $[20] = plan;
    $[21] = planRemaining;
    $[22] = t;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== creditsBalance || $[25] !== hasNoCredits || $[26] !== isChatPlanOrg || $[27] !== isLowCredits || $[28] !== organization?.id || $[29] !== t) {
    t7 = !isChatPlanOrg && <TopUpCreditsDialog organizationId={organization?.id}><button className="w-full flex items-center justify-between p-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left"><div className="flex items-center gap-2"><CreditCard className={`h-4 w-4 ${hasNoCredits ? "text-destructive" : isLowCredits ? "text-yellow-500" : "text-muted-foreground"}`} /><div className="flex flex-col"><span className="text-sm font-medium">{t("billing.credits")}</span><span className={`text-xs ${hasNoCredits ? "text-destructive" : isLowCredits ? "text-yellow-600" : "text-muted-foreground"}`}>${creditsBalance}</span></div></div><span className="text-xs text-muted-foreground">{t("common.add")}</span></button></TopUpCreditsDialog>;
    $[24] = creditsBalance;
    $[25] = hasNoCredits;
    $[26] = isChatPlanOrg;
    $[27] = isLowCredits;
    $[28] = organization?.id;
    $[29] = t;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== hasActivePlan || $[32] !== isChatPlanOrg || $[33] !== t) {
    t8 = isChatPlanOrg && !hasActivePlan && <Link href="/pricing" className="group mt-1 flex items-center gap-2.5 rounded-md border border-indigo-500/20 bg-indigo-500/5 px-2 py-2 transition-colors hover:bg-indigo-500/10"><span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"><TrendingUp className="size-3.5" /></span><div className="flex min-w-0 flex-col"><span className="text-xs font-medium leading-tight">{t("billing.upgradeCredits")}</span><span className="text-[10px] text-muted-foreground leading-tight">{t("billing.upgradeMonthly")}</span></div><ChevronRight className="ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" /></Link>;
    $[31] = hasActivePlan;
    $[32] = isChatPlanOrg;
    $[33] = t;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== hasNoCredits || $[36] !== isChatPlanOrg || $[37] !== t) {
    t9 = !isChatPlanOrg && hasNoCredits && <div className="mt-1 px-2"><p className="text-xs text-destructive">⚠️ {t("billing.noCredits")}</p></div>;
    $[35] = hasNoCredits;
    $[36] = isChatPlanOrg;
    $[37] = t;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== hasNoCredits || $[40] !== isChatPlanOrg || $[41] !== isLowCredits || $[42] !== t) {
    t10 = !isChatPlanOrg && isLowCredits && !hasNoCredits && <div className="mt-1 px-2"><p className="text-xs text-yellow-600">⚡ {t("billing.lowCredits")}</p></div>;
    $[39] = hasNoCredits;
    $[40] = isChatPlanOrg;
    $[41] = isLowCredits;
    $[42] = t;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t6 || $[46] !== t7 || $[47] !== t8 || $[48] !== t9) {
    t11 = <div className="px-2 py-1.5">{t6}{t7}{t8}{t9}{t10}</div>;
    $[44] = t10;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  return t11;
}